import UAParser from "ua-parser-js";
import { differenceInMinutes, format, formatRelative } from "date-fns";
import { get, isEmpty, set } from "lodash";
import showdown from "showdown";
import mime from "mime-types";
import queryString from "query-string";
import constants from "./constants";
import Store from "./Store";
import { getMessaging, getToken } from "firebase/messaging";
import { User, UserInfo } from "../types/api/UserInfo";
// @ts-ignore
import { v4 as uuidv4 } from "uuid";
import { enUS } from "date-fns/locale";
import { datadogRum } from "@datadog/browser-rum";
import getEnv from "./getEnv";
import { t } from "i18next";

export const getGender = (value?: number) => {
  return constants.GENDERS.find((g) => g.value == value);
};

export const getUserAgent = (providedUserAgent?: string) => {
  const parser = new UAParser(providedUserAgent);
  return parser.getResult();
};

export const isIPad = () => {
  const platform = navigator?.platform;
  const userAgent = navigator?.userAgent;
  return (
    (platform === "iPad" || (platform === "MacIntel" && navigator?.maxTouchPoints > 1)) &&
    /iPad|Macintosh|iPhone|iOS/i.test(userAgent)
  );
};

export const isAndroidWebView = () => {
  const userAgent = getUserAgent();
  const isAndroidOS = userAgent.os.name === "Android";
  const hasVersion = userAgent.ua.toLowerCase().includes("version");
  return isAndroidOS && hasVersion;
};

export const isIOSWebView = () => {
  const userAgent = getUserAgent();
  const hasSupportedModel =
    userAgent.device.vendor === "Apple" &&
    (userAgent.device.model === "iPhone" ||
      userAgent.device.model === "iPod" ||
      userAgent.device.model === "iPad");
  const hasSupportedEngine = userAgent.engine.name === "WebKit";
  const isNotBrowser = !userAgent.browser.name?.includes("Safari");
  return hasSupportedModel && hasSupportedEngine && isNotBrowser;
};

export const isWebView = () => isAndroidWebView() || isIOSWebView();

export const getCurrentRegion = () => {
  const _split = window?.location?.host.split(".");
  let region = "global";
  if (
    _split.length > 1 &&
    getEnv("NEXT_PUBLIC_REGIONS")?.split(",")?.includes(_split[0])
  ) {
    region = _split[0];
  }
  return region;
};

export const getPlatform = () => {
  if (isAndroidWebView()) {
    return constants.PLATFORMS.ANDROID;
  }
  if (isIOSWebView()) {
    return constants.PLATFORMS.IOS;
  }
  return constants.PLATFORMS.PWA;
};

export const isValidDate = (date: Date) => {
  return date instanceof Date && !Number.isNaN(date.getTime());
};

export const getFormattedDate = (
  date: string,
  dateFormat = "E PP hh:mm a",
  defaultValue = "NA"
) => {
  const dateVal = new Date(date);
  return date && isValidDate(dateVal) ? format(dateVal, dateFormat) : defaultValue;
};

export const truncateMiddle = (
  fullStr: string,
  strLen: number,
  separatorParam?: string
) => {
  if (isEmpty(fullStr) || fullStr.length <= strLen) {
    return fullStr;
  }

  const separator = separatorParam || "...";

  const sepLen = separator.length;
  const charsToShow = strLen - sepLen;
  const frontChars = Math.ceil(charsToShow / 2);
  const backChars = Math.floor(charsToShow / 2);

  return (
    fullStr.substr(0, frontChars) + separator + fullStr.substr(fullStr.length - backChars)
  );
};

export const getNameInitials = (name?: string) => {
  if (!!name) {
    const nameSplit = name.split(/\s+|_|(?=[A-Z])/);
    let first = get(nameSplit, [0, 0], "");
    let last = get(nameSplit, [1, 0], "");
    return `${first.toUpperCase()}${last.toUpperCase()}`;
  }
  return "-";
};

export const convertMarkdownToHtml = (markdownString: string) => {
  const converter = new showdown.Converter({
    simpleLineBreaks: true,
    underline: true,
    tables: true,
  });
  markdownString = markdownString?.replaceAll("\n", "<br />");
  const htmlString = converter.makeHtml(markdownString);
  return htmlString;
};

export const isDevEnv = () => {
  return getEnv("NODE_ENV") === "development";
};

export const getUrlAuthToken = () => {
  const params = queryString.parse(window?.location?.search);
  return get(params, ["url_auth_token"], "");
};

export const getMessageType = (
  media_uid: number | null,
  studyUid: number | null,
  reportUid: number | null,
  formResponseUid: number | null,
  questionnaire_uid: number | null,
  questionnaire_response_uid: number | null,
  patient_report_sharing_status_uid: number | null
) => {
  if (media_uid) {
    return constants.MESSAGE_TYPES.MEDIA;
  } else if (studyUid) {
    return constants.MESSAGE_TYPES.STUDY;
  } else if (reportUid) {
    return constants.MESSAGE_TYPES.REPORT;
  } else if (formResponseUid) {
    return constants.MESSAGE_TYPES.FORM;
  } else if (questionnaire_uid) {
    return constants.MESSAGE_TYPES.QUESTION;
  } else if (questionnaire_response_uid) {
    return constants.MESSAGE_TYPES.ANSWER;
  } else if (patient_report_sharing_status_uid) {
    return constants.MESSAGE_TYPES.REPORT_SHARING;
  }
  return "";
};

export const getMediaType = (mimeType: string) => {
  const images = constants.IMAGE_TYPES;
  const docs = constants.DOC_TYPES;
  const audios = constants.AUDIO_TYPES;
  const extension = mime.extension(mimeType) || "";

  if (images.includes(extension)) {
    return { type: constants.MEDIA_TYPES.IMAGE, extension };
  } else if (docs.includes(extension)) {
    return { type: constants.MEDIA_TYPES.DOCUMENT, extension };
  } else if (audios.includes(extension)) {
    return { type: constants.MEDIA_TYPES.AUDIO, extension };
  } else {
    return { type: constants.MEDIA_TYPES.NA, extension };
  }
};

export const isNonDicomFile = (fileType: string | undefined) => {
  const fileTypes = ["png", "jpg", "jpeg"];
  return fileTypes.some((type) => fileType?.toLowerCase()?.includes(type));
};

export const isValidEmail = (text: string) => {
  return /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/.test(
    text
  );
};

const DEMO_SOURCE_ID = ["demo@qure.ai", "demo.qure.ai"];

export const isDemoSource = (sourceId: string) => {
  return DEMO_SOURCE_ID.includes(sourceId);
};

export const convertBufferToFile = (arrayBuffer: ArrayBuffer, fileName?: string) => {
  const blob = new Blob([arrayBuffer], { type: "application/octet-stream" });
  return new File([blob], fileName ? fileName : "anonymous", {
    type: "application/octet-stream",
  });
};

export const getCookies = () => {
  const _cookies: string[] = document.cookie.split("; ");
  const allCookies = {};
  _cookies.forEach((cookie) => {
    const [key, val] = cookie.split("=");
    set(allCookies, [key], val);
  });
  return allCookies;
};

export const generatePatientId = () => {
  const uuidPart = uuidv4().split("-").pop();
  const timePart = Math.floor(Date.now() / 1000).toString(); // Using Date.now() for milliseconds and converting to seconds
  const patientId = (uuidPart + timePart).toUpperCase();
  return patientId;
  // return Math.random().toString(36).slice(2).toUpperCase();
};

export const getFCMToken = async () => {
  return await getToken(getMessaging(), {
    vapidKey: getEnv("NEXT_PUBLIC_VAPID"),
  });
};

export function getIsOnpremise() {
  try {
    const isOnpremise = process.env.NEXT_PUBLIC_IS_ONPREMISE; // not using getEnv() because it's not getting resolved on few browsers while initial rendering
    if (isOnpremise !== undefined && isOnpremise.toLowerCase() === "true") {
      return true;
    }
    return false;
  } catch (error) {
    return false;
  }
}

export function getIsRemoteSystem() {
  const isOnpremise = getIsOnpremise();
  return isOnpremise && window.location.hostname !== "localhost";
}

export function getIsPatientDeletionEnabled() {
  try {
    const isPatientDeletionEnabled = getEnv("NEXT_PUBLIC_IS_PATIENT_FILE_DELETION");
    if (isPatientDeletionEnabled?.toLowerCase() === "true") {
      return true;
    }
    return false;
  } catch (error) {
    return false;
  }
}

export const getAllSources = (
  userInfo: UserInfo
): {
  label: string;
  value: number;
  uuid: string;
}[] => {
  const sources = get(userInfo, ["upload_sources"], []);
  const listSource = sources.map((src) => {
    return {
      label: src["name"],
      value: src["id"],
      uuid: src["uuid"],
    };
  });

  return listSource;
};

export const getSource = (
  userInfo: UserInfo
): {
  label: string;
  value: number;
  uuid: string;
} => {
  const sources = get(userInfo, ["upload_sources"], []);
  const listSource = sources.map((src) => {
    return {
      label: src["name"],
      value: src["id"],
      uuid: src["uuid"],
    };
  });

  const default_workspace = listSource.find(
    (item) => item.value === userInfo?.default_workspace
  );

  return default_workspace ?? listSource[0];
};

export const getTimeDifference = (date: Date = new Date()) => {
  const timeDifference = differenceInMinutes(new Date(), date);
  let formattedDifference;
  if (timeDifference < 60) {
    formattedDifference = `${timeDifference}m`;
  } else {
    const hours = Math.floor(timeDifference / 60);
    const minutes = timeDifference % 60;
    if (hours < 24) {
      formattedDifference = `${hours}h ${minutes}m`;
    } else {
      const days = Math.floor(timeDifference / 1440);
      const _hours = hours % 24;
      formattedDifference = `${days}d ${_hours}h ${minutes}m`;
    }
  }
  return formattedDifference;
};

export const getFormattedRelativeDateTimeDifference = (date: string) => {
  if (date) {
    const formatRelativeLocale: Record<string, any> = {
      yesterday: `'${t("yesterday_key")}, ' p`,
      today: `'${t("today_key")}, ' p`,
      lastWeek: "PP hh:mm a",
      tomorrow: "PP hh:mm a",
      nextWeek: "PP hh:mm a",
      other: "PP hh:mm a",
    };
    const locale = {
      ...enUS,
      formatRelative: (token: string) => formatRelativeLocale[token],
    };
    const _date = new Date(date);
    return formatRelative(_date, new Date(), { locale });
  }
};

export const getDateFromDatetime = (value: Date | string) => {
  let _value = new Date(value);
  return new Date(_value.getFullYear(), _value.getMonth(), _value.getDate(), 0, 0, 0, 0);
};

export const parseAgeInYearsFromDicomAttribute = (age: string) => {
  if (!isNaN(+age)) {
    return parseInt(age, 10);
  }
  /*
   * If age is of the form 'X1X2...XnM' where X1,2..n are digits
   * and M is one of measures = {Y(years), M(months), D(days)}.
   * Get the measure and convert rest of the string into number
   */

  const measure = age.slice(-1);
  const modifiedAge = age.slice(0, -1);

  if (isNaN(+modifiedAge)) {
    return 0;
  }

  switch (measure) {
    case "Y":
      return parseInt(modifiedAge, 10);

    case "M":
      return Math.floor(parseInt(modifiedAge, 10) / 12);

    case "W":
      return Math.floor(parseInt(modifiedAge, 10) / 52);

    case "D":
      return Math.floor(parseInt(modifiedAge, 10) / 365);

    default:
      return parseInt(age, 10);
  }
};

export const getMetabaseURL = () => {
  const isOnpremise = getIsOnpremise();

  if (typeof window === "undefined") {
    return getEnv("NEXT_PUBLIC_API_URL");
  }

  if (!isOnpremise) {
    return getEnv("NEXT_PUBLIC_API_URL");
  }

  const hostname = window?.location.hostname;
  return `http://${hostname}:${getEnv("NEXT_PUBLIC_ONPREMISE_METABASE_PORT")}`;
};

export const getMWLURL = () => {
  const isOnpremise = getIsOnpremise();

  if (typeof window === "undefined") {
    return getEnv("NEXT_PUBLIC_API_URL");
  }

  if (!isOnpremise) {
    return getEnv("NEXT_PUBLIC_API_URL");
  }

  const hostname = window?.location.hostname;
  return `http://${hostname}:${getEnv("NEXT_PUBLIC_ONPREMISE_MWL_PORT")}`;
};

export const getTrimmedWorkspaceId = (id: string) => {
  return id && id.slice(-8);
};

export const getAppLogo = (): string => {
  const isOnpremise = getIsOnpremise();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const isqTrack = Store.useState((store) => store.isqTrack);
  if (isOnpremise || isqTrack) {
    return getEnv("NEXT_PUBLIC_APP_LOGO") || "/assets/images/qTrackLogo.svg";
  }

  return "/assets/images/qureLogo.svg";
};

export const hasSiteKeyword = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const isqTrack = Store.useState((store) => store.isqTrack);
  return getEnv("NEXT_PUBLIC_HAS_SITES_KEYWORD") === "true" || isqTrack;
};

export const trackError = (
  errorMessage: string,
  url: string,
  scope?: string | undefined,
  additionalInfo?: {}
) => {
  const error = new Error(errorMessage);

  datadogRum.addError(error, {
    scope: scope,
    url: url,
    ...additionalInfo,
  });
};

export const urlify = (text: string) => {
  if (text) {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return text.replace(
      urlRegex,
      '<a class="underline !text-chat-7" href="$1" target="_blank">$1</a>'
    );
  } else {
    return "";
  }
};

export const formatDate = (dateString: string) => {
  let year = Number(dateString.substring(0, 4));
  let month = Number(dateString.substring(4, 6));
  let day = Number(dateString.substring(6, 8));
  let date = new Date(year, month - 1, day);
  return format(date, "dd MMM yyyy");
};

export const getUserName = (user: User | null) => {
  return `${user?.name}`;
};

export function extractWorkspaceToken(url: string): string | null {
  try {
    const urlObj = new URL(url);
    return urlObj.searchParams.get("workspace-token");
  } catch (error) {
    console.error("Invalid URL:", error);
    return null;
  }
}

export const isSingleImageModality = (modality: string) => {
  const supportedModalities = ["CR", "MG", "DX", "DR", "CX"];
  return supportedModalities.includes(modality);
};
